/**
 * @Copyright 2014-2017 DataVoke, Inc. All rights reserved.
 */

'use strict';

import {
    ClientProperties,
    EntityBase
} from './entitybase';
import {
    PropertyDataType,
    EntityStates
} from '../utils/dvtypes';
import {
    deepClone,
    tempID
} from '../utils/tools';

export class DynamicValue extends EntityBase {
    /**
     * Create a managed data object for the specified record ID and for the specified property.
     * @param {string|number|Date|null} value - The val1ue for this class to manage.
     * @param {object} property - The property that this instanced data value is for.
     * @param {number} recordID - The ID of the record that this instanced data value is for.
     * @param {object} [clientValue = null] - This is the global source value instance for this form-level instance of this managed data.
     * @param {ValueStatus|null} [status = null] - The value status of this dynamic value (default to null)
     * @class
     * @classdesc Dynamic Value Object
     */
    constructor (value, property, recordID, clientValue = null, status = null) {
        super();

        /**
         * A string to identify this type of object.
         * @type {string}
         */
        this.typeName = "datavoke.entities.dynamicValue";

        /**
         * The typed value ID for the value managed by this class.
         * @type {number}
         */
        this.TypeValueID = tempID();

        /**
         * The property ID for this value...AL
         * @type {number}
         */
        this.PropertyID = (property != null ? property.PropertyID : 0);

        /**
         * The record ID for this value...AL
         * @type {number}
         */
        this.RecordID = (recordID != null ? recordID : 0);

        /**
         * The data type of the value (defualt to text)...AL
         * @type {PropertyDataType}
         */
        this.DataType = (property != null ? property.Definition.DataType : PropertyDataType.Text);

        /**
         * Value for the timezone offset of a DateTimeValue or LocalDateValue...AL
         * @type {number|null}
         */
        this.TimeZoneOffset = null;

        /**
         * The value that is managed by this DynamicValue class.
         * @type {string|number|Date|null}
         */
        this.Value = null;

        switch (this.DataType) {
            case PropertyDataType.Bit:
                if (typeof value == 'string') {
                    if (property.Definition.AllowNulls) {
                        // deal with string conversion of boolean data (column defaults, etc.).
                        switch (value.toLowerCase()) {
                            case "true":
                            case "1":
                            case "yes":
                            case "y":
                                this.Value = true;
                                break;
                            case "false":
                            case "0":
                            case "no":
                            case "n":
                                this.Value = false;
                                break;
                            case "":
                            default:
                                this.Value = null;
                                break;
                        }
                    }
                    else {
                        // deal with string conversion of boolean data (column defaults, etc.).
                        switch (value.toLowerCase()) {
                            case "true":
                            case "1":
                            case "yes":
                            case "y":
                                this.Value = true;
                                break;
                            case "false":
                            case "0":
                            case "no":
                            case "n":
                            case "":
                            default:
                                this.Value = false;
                                break;
                        }
                    }
                }
                else {
                    if (property.Definition.AllowNulls) {
                        this.Value = value;
                    }
                    else {
                        // do not allow the value to be null (or undefined).
                        this.Value = value != null ? value : false;
                    }
                }
                break;

            case PropertyDataType.Decimal:
            case PropertyDataType.Integer:
                if (value === "") {
                    value = null;
                }
                this.Value = value;
                break;

            case PropertyDataType.DateTime:
            case PropertyDataType.StaticDateTime:
                if (typeof value === 'string') {
                    if (value === "") {
                        // no string, no date.
                        this.Value = null;
                    }
                    else {
                        // handle string conversion to a date.
                        this.Value = Date.parse(value);
                    }
                }
                else {
                    this.Value = value;
                }
                break;

            case PropertyDataType.Binary:
                // binary (file) values have no value, only meta data.
                this.Value = null;
                break;

            default:
                // The actual raw value of this dynamic value...AL
                this.Value = value;
        }

        /**
         * The metadata for this dynamic value...AL Only used if this is a Binary value.
         * @type {string}
         */
        this.ValueMetadata = "";

        /**
         * The value status of this dynamic value (default to null)...AL
         * @type {ValueStatus|null}
         */
        this.Status = status ? status : null;
    }

    /**
     * Create a ES6 DynamicValue class instance from the specified object.
     * @param {object} obj - An object whose properties will be used to populate a DynamicValue object
     * @returns {DynamicValue}
     */
    static convertFrom (obj) {
        let props = Object.getOwnPropertyDescriptors(obj),
            dv = Object.create(DynamicValue.prototype, props);

        //#region Create any missing properties.
        if (dv.typeName == null) {
            dv.typeName = 'datavoke.entities.dynamicValue'
        }

        if (dv.TypeValueID == null) {
            dv.TypeValueID = tempID();
        }

        if (dv.PropertyID == null) {
            // this will actually create the property if undefined.
            dv.PropertyID = 0;
        }

        if (dv.RecordID == null) {
            dv.RecordID = 0;
        }

        if (dv.DataType == null) {
            dv.DataType = PropertyDataType.Text;
        }

        if (dv.TimeZoneOffset == null) {
            // this will actually create the property if undefined.
            dv.TimeZoneOffset = null;
        }

        if (dv.Value == null) {
            // this will actually create the property if undefined.
            dv.Value = null;
        }

        if (dv.ValueMetadata == null) {
            // this will actually create the property if undefined.
            dv.ValueMetadata = '';
        }

        if (dv.Status == null) {
            // this will actually create the property if undefined.
            dv.Status = null;
        }
        //#endregion

        return dv;
    }

    /**
     * Create a clone of the specified object in preparation for JSON stringifying. Any unnecessary properties will be removed.
     * @param {DynamicValue} dv
     * @returns {object}
     */
    static cloneForTransmission (dv) {
        let obj = {
            "_State": dv._State,
            "Status": dv.Status || 0
        };

        if (dv._State === EntityStates.NewObject) {
            // These properties are _only_ needed on transmissions of a "new" DynamicValue object.
            obj.UID = dv.UID;
        }

        // These properties are included on _all_ transmissions of a DynamicValue object.
        obj.DataType = dv.DataType;
        obj.TypeValueID = dv.TypeValueID;
        obj.Value = dv.Value;
        obj.PropertyID = dv.PropertyID;
        obj.RecordID = dv.RecordID;

        if (dv.ValueMetadata != null && dv.ValueMetadata !== '') {
            // if this value has meta data - include it.
            obj.ValueMetadata = dv.ValueMetadata;
        }

        if (obj.Value != null
            && (dv.DataType === PropertyDataType.DateTime
                || dv.DataType === PropertyDataType.StaticDateTime)) {

            // ensure that the date is converted to a "UTC" date value by applying the offset.
            obj.Value = new Date(obj.Value);
            obj.Value.addMinutes(0 - obj.Value.getTimezoneOffset());
        }

        if (dv.TimeZoneOffset != null) {
            // if this value as a time zone offset - include it.
            obj.TimeZoneOffset = dv.TimeZoneOffset;
        }

        return obj;
    }
}

/**
 * Expose a function to return a new DynamicValue object.
 * @param {string|number|Date|null} value - The value for the dyanmic value object.
 * @param {object} property - The property that this value is for.
 * @param {number} recordID - The ID of the record that this value is for.
 * @param {object} [clientValue = null] - This is the global source value instance for the dynamic value.
 * @param {ValueStatus|null} [status = null] - The value status of this dynamic value (default to null).
 * @returns {DynamicValue}
 */
export function create(value, property, recordID, clientValue, status) {
    return new DynamicValue(value, property, recordID, clientValue, status);
}
